import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DwComponent, DwComponentType, DwContextSubjects, DwMdFormContextInfo, DwMetaDataFormConfig,
    DwMetaDataService, DwMetaDataServiceToken, DwOrmDataService, DwOrmDataServiceToken,
    UserMdItemSecurity, DwMediaSelectOptions, MediaMetaDataLookups, DwMediaModalService, DwMediaConfig, DwMediaConfigToken } from '@devwareapps/devware-cap';
import { combineLatest, defer, Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { AppMetaData, DwProductMediaEntity, DwProductColorEntity } from '../../meta-data/app-meta-data.service';
import { moveMedia, nextMediaName } from './product-media.helpers';

@DwComponent({ key: 'fitness-product-media-gallery', name: 'Product image gallery',
    componentType: DwComponentType.formSection, isGlobal: true })
@Component({ selector: 'app-product-media-gallery', templateUrl: './product-media-gallery.component.html',
    styleUrls: ['./product-media-gallery.component.scss'] })
export class ProductMediaGalleryComponent implements OnInit, OnDestroy {
    @Input() formConfig: DwMetaDataFormConfig;
    @Input() sectionConfig: any;
    @Input() set context(context: DwContextSubjects) {
        this.contextSubscriptions.unsubscribe(); this.contextSubscriptions = new Subscription();
        if (!context?.data || !context?.mdFormContext) { return; }
        this.contextSubscriptions.add(combineLatest([context.data, context.mdFormContext]).subscribe(([data, form]) => {
            this.formContext = form;
            const productId = form?.loadDataComplete ? Number(data?.ProductId) || 0 : 0;
            this.productName = data?.ProductName || 'Product';
            if (productId !== this.productId) {
                this.productId = productId; this.rows = []; this.error = ''; this.message = '';
                this.load();
            }
        }));
    }
    rows: DwProductMediaEntity[] = [];
    productId = 0;
    productName = 'Product';
    loading = false;
    busy = false;
    uncertain = false;
    error = '';
    message = '';
    schemaMessage = '';
    readonly mediaIdAttribute = AppMetaData.DwProductMedia.Attributes.MediaId.fieldName;
    hasMediaId = false;
    nameMaxLength = 50;
    webImageTypeId = 0;
    permissions: Partial<UserMdItemSecurity> = {};
    draggedId = 0;
    dropActive = false;
    formContext: DwMdFormContextInfo;
    private ready = false;
    private contextSubscriptions = new Subscription();
    private loadSubscription = new Subscription();
    private destroyed = new Subject<void>();

    constructor(@Inject(DwOrmDataServiceToken) private orm: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private metadata: DwMetaDataService,
        private mediaDialogs: DwMediaModalService,
        @Inject(DwMediaConfigToken) private mediaConfig: DwMediaConfig) {}

    ngOnInit(): void {
        const configuredMediaAttribute = this.sectionConfig?.config?.mediaIdAttribute;
        const compatibleMediaAttribute = !configuredMediaAttribute || configuredMediaAttribute === this.mediaIdAttribute;
        combineLatest([this.metadata.getMetaData(AppMetaData.DwProductMedia.ItemDetail.itemName),
            this.metadata.getUserMetaDataItemSecurity(AppMetaData.DwProductMedia.ItemDetail.itemName),
            this.loadWebImageType()]).pipe(take(1), takeUntil(this.destroyed))
            .subscribe(([metadata, permissions, imageTypeId]) => {
                this.permissions = permissions;
                this.hasMediaId = compatibleMediaAttribute && metadata.Attributes.some(a => a.AttributeName === this.mediaIdAttribute && !a.IsKey && a.IsActive !== false);
                this.nameMaxLength = metadata.Attributes.find(a => a.AttributeName === AppMetaData.DwProductMedia.Attributes.MediaName.fieldName)?.MaxLength || 50;
                this.webImageTypeId = imageTypeId;
                if (!this.hasMediaId) {
                    this.schemaMessage = 'Image uploads require the ' + this.mediaIdAttribute + ' field in Product Media metadata.';
                } else if (!this.webImageTypeId) { this.schemaMessage = 'The Web Images media type is unavailable.'; }
                this.ready = true; this.load();
            }, () => { this.error = 'Unable to load image configuration. Refresh the Product form.'; });
    }

    get editable(): boolean {
        return !!this.productId && this.formContext?.loadDataComplete && !this.formContext.isReadOnly &&
            !this.formConfig?.readOnly && ['edit', 'addEdit'].includes(this.formContext.contextMode);
    }
    get locked(): boolean { return this.busy || this.loading || this.uncertain || !this.editable; }
    get canUpload(): boolean {
        return !this.locked && this.hasMediaId && !!this.webImageTypeId && !!this.permissions.CanCreate;
    }
    // CAP's image control caches loaded media; recreate the card when its image ID changes.
    trackRow = (index: number, row: DwProductMediaEntity): string => row.ProductMediaId + ':' + this.imageId(row);
    imageId(row: DwProductMediaEntity): number { return Number(row.MediaId) || 0; }

    load(): void {
        this.loadSubscription.unsubscribe(); this.loadSubscription = new Subscription();
        if (!this.ready || !this.productId) { this.loading = false; return; }
        const productId = this.productId;
        this.loading = true;
        this.loadSubscription.add(this.loadImages(productId).subscribe(rows => {
            if (productId !== this.productId) { return; }
            this.rows = rows.sort((a, b) => (a.MediaOrder ?? 2147483647) - (b.MediaOrder ?? 2147483647) || a.ProductMediaId - b.ProductMediaId);
            this.loading = false; this.uncertain = false; this.error = '';
        }, () => { this.loading = false; this.error = 'Unable to load product images. Try refreshing.'; this.uncertain = true; }));
    }
    refresh(): void { if (!this.busy) { this.load(); } }

    saveName(row: DwProductMediaEntity, name: string): void {
        if (this.locked || !this.permissions.CanUpdate) { return; }
        name = name.trim();
        if (!name || name.length > this.nameMaxLength) { this.error = 'Enter a media name within the allowed length.'; return; }
        if (this.rows.some(other => other.ProductMediaId !== row.ProductMediaId && other.MediaName?.trim().toLocaleLowerCase() === name.toLocaleLowerCase())) {
            this.error = 'Another image already uses that name.'; return;
        }
        if (name === row.MediaName) { return; }
        this.run(() => this.orm.saveEntity(this.entity(row.ProductMediaId, { MediaName: name })), 'Image name saved.');
    }
    setDefault(row: DwProductMediaEntity, checked: boolean): void {
        if (this.locked || !this.permissions.CanUpdate) { return; }
        const changes = this.rows.filter(other => !!other.IsDefault !== (other.ProductMediaId === row.ProductMediaId && checked))
            .map(other => this.entity(other.ProductMediaId, { IsDefault: other.ProductMediaId === row.ProductMediaId && checked }));
        this.run(() => this.orm.saveEntityCollection(changes), 'Default image saved.');
    }
    move(from: number, to: number): void {
        if (this.locked || !this.permissions.CanUpdate || from === to || to < 0 || to >= this.rows.length) { return; }
        const ordered = moveMedia(this.rows, from, to);
        const changes = ordered.map((row, index) => this.entity(row.ProductMediaId, { MediaOrder: index + 1 }));
        this.run(() => this.orm.saveEntityCollection(changes), 'Image order saved.');
    }
    startDrag(event: DragEvent, row: DwProductMediaEntity): void {
        if (this.locked || !this.permissions.CanUpdate) { event.preventDefault(); return; }
        this.draggedId = row.ProductMediaId;
        event.dataTransfer.setData('text/plain', String(row.ProductMediaId));
        event.dataTransfer.effectAllowed = 'move';
    }
    overCard(event: DragEvent): void { if (this.draggedId) { event.preventDefault(); } }
    dropCard(event: DragEvent, target: number): void {
        if (!this.draggedId) { return; }
        event.preventDefault(); event.stopPropagation();
        const source = this.rows.findIndex(row => row.ProductMediaId === this.draggedId);
        this.draggedId = 0; this.move(source, target);
    }
    overUpload(event: DragEvent): void {
        if (this.canUpload && event.dataTransfer?.types.includes('Files')) {
            event.preventDefault(); event.dataTransfer.dropEffect = 'copy'; this.dropActive = true;
        }
    }
    dropFiles(event: DragEvent): void {
        event.preventDefault(); this.dropActive = false;
        if (this.canUpload && event.dataTransfer?.files.length) { this.upload(event.dataTransfer.files); }
    }
    upload(files?: FileList): void {
        if (!this.canUpload) { return; }
        const productId = this.productId;
        const productName = this.productName;
        this.run(() => this.mediaDialogs.showMultipleFileModal(this.uploadOptions(), undefined, files).pipe(take(1), switchMap(result => {
            if (!result?.media?.length) { return of(null); }
            if (this.productId !== productId || !this.editable) {
                return throwError(new Error('The Product form changed while uploading. Reopen the product to continue.'));
            }
            // Reload before assigning names/order; another gallery may have added images during the dialog.
            return this.loadImages(productId).pipe(switchMap(existing => {
                if (this.productId !== productId || !this.editable) { throw new Error('The Product form changed while uploading.'); }
                const names = existing.map(row => row.MediaName);
                let order = Math.max(0, ...existing.map(row => Number(row.MediaOrder) || 0));
                const hasDefault = existing.some(row => row.IsDefault);
                const entities = result.media.map((media, index) => {
                    if (!Number.isSafeInteger(Number(media.MediaId)) || Number(media.MediaId) <= 0) { throw new Error('Upload did not return a media ID.'); }
                    const name = nextMediaName(productName, names, this.nameMaxLength); names.push(name);
                    return this.entity(null, { ProductId: productId, MediaId: media.MediaId,
                        MediaName: name, MediaTypeId: this.webImageTypeId, MediaOrder: ++order, IsDefault: !hasDefault && index === 0 });
                });
                return this.orm.saveEntityCollection(entities);
            }));
        })), 'Images added.');
    }
    replace(row: DwProductMediaEntity): void {
        if (this.locked || !this.permissions.CanUpdate || !this.hasMediaId) { return; }
        const productId = this.productId;
        this.run(() => this.mediaDialogs.showSelectFileModal(this.uploadOptions()).pipe(take(1), switchMap(media => {
            if (!media) { return of(null); }
            if (this.productId !== productId || !this.editable) { throw new Error('The Product form changed while uploading.'); }
            if (!Number.isSafeInteger(Number(media.MediaId)) || Number(media.MediaId) <= 0) { throw new Error('Upload did not return a media ID.'); }
            return this.orm.saveEntity(this.entity(row.ProductMediaId, { MediaId: media.MediaId,
                MediaUrl: null, MediaTypeId: this.webImageTypeId }));
        })), 'Image replaced.');
    }
    remove(row: DwProductMediaEntity): void {
        if (this.locked || !this.permissions.CanDelete) { return; }
        const productId = this.productId;
        this.run(() => this.loadImageColors(row.ProductMediaId).pipe(switchMap(colors => {
            if (colors.length) { throw new Error('This image is used by a product color. Change that color image before removing it.'); }
            return this.mediaDialogs.showRemoveFileConfigDialog({ showConfirmDialog: true, useConfirmDefaults: false,
                title: 'Remove product image', message: 'Remove this image from the product? The uploaded file will remain available.' })
                .pipe(take(1), switchMap(confirmed => {
                    if (!confirmed) { return of(null); }
                    if (this.productId !== productId || !this.editable) { throw new Error('The Product form changed.'); }
                    return this.orm.deleteEntity(this.entity(row.ProductMediaId, {}));
                }));
        })), 'Image removed.');
    }

    private uploadOptions(): DwMediaSelectOptions {
        return { allowedFileTypes: this.mediaConfig.webImageFileExtensions || 'jpg,jpeg,png,gif,webp',
            isSecure: false, parentItemName: AppMetaData.DwProductMedia.ItemDetail.itemName, parentAttributeName: this.mediaIdAttribute,
            uploadInstructions: 'Select product images. Names and order are assigned after upload.' };
    }
    private entity(id: number, values: Partial<DwProductMediaEntity>): DwProductMediaEntity {
        const entity = AppMetaData.DwProductMedia.CreateEntity();
        if (id) { entity.ProductMediaId = id; }
        return Object.assign(entity, values);
    }
    private loadWebImageType(): Observable<number> {
        return this.metadata.getLookup(MediaMetaDataLookups.MediaTypeAllItems).pipe(take(1), map(types => {
            const matches = types.filter(type => type.Display === 'Web Images');
            const id = matches.length === 1 ? Number(matches[0].Id) : 0;
            return Number.isSafeInteger(id) && id > 0 ? id : 0;
        }));
    }
    private loadImages(productId: number): Observable<DwProductMediaEntity[]> {
        const query = AppMetaData.DwProductMedia.CreateQueryBuilder()
            .addFilterAnd(f => f.Equal(a => a.ProductId, productId))
            .addOrderBy(a => a.MediaOrder).addOrderBy(a => a.ProductMediaId);
        return this.orm.executeQuery<DwProductMediaEntity>(query).pipe(take(1));
    }
    private loadImageColors(productMediaId: number): Observable<DwProductColorEntity[]> {
        const query = AppMetaData.DwProductColor.CreateQueryBuilder().addFields(a => [a.ProductColorId])
            .addFilterAnd(f => f.Equal(a => a.ProductMediaId, productMediaId));
        return this.orm.executeQuery<DwProductColorEntity>(query).pipe(take(1));
    }
    private run<T>(operation: () => Observable<T>, message: string): void {
        if (this.locked) { return; }
        const productId = this.productId;
        this.busy = true; this.error = ''; this.message = '';
        defer(operation).pipe(take(1), takeUntil(this.destroyed), finalize(() => this.busy = false)).subscribe(result => {
            if (this.productId === productId && result != null) { this.message = message; this.load(); }
        }, error => {
            if (this.productId === productId) {
                this.uncertain = true;
                this.error = (error instanceof Error ? error.message + ' ' : '') + 'Refresh images before trying again. Uploaded files are retained if saving fails.';
            }
        });
    }
    ngOnDestroy(): void {
        this.destroyed.next(); this.destroyed.complete(); this.contextSubscriptions.unsubscribe(); this.loadSubscription.unsubscribe();
    }
}
